import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Button } from '@keobook/ui';
import { streamChatTurn } from '../lib/chat-stream';

/**
 * Customer chat — collapsible side panel. Asks the API to stream a grounded
 * answer to a free-form question about the user's books.
 *
 * Session lifecycle:
 *   - Mounts with no session (sessionId = null).
 *   - First user turn either creates a new session server-side OR re-uses
 *     `currentSessionId` if the user picked an existing one from the list.
 *   - The SSE `session` event lands first and latches sessionId on local
 *     state so subsequent turns continue the same conversation.
 *
 * What it deliberately does NOT do (yet):
 *   - Session list / picker. Out of scope for v1; the user only sees the
 *     active session. (Sessions are still saved server-side — picker just
 *     not wired.)
 *   - Stop button. The first cut just lets the stream complete. Adding
 *     abort needs the stream helper to expose its AbortController.
 */
export function ChatPanel() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [messages, setMessages] = useState<
    Array<{ id: string; role: 'user' | 'assistant'; content: string }>
  >([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [messages]);

  async function send(e: FormEvent) {
    e.preventDefault();
    if (busy || !input.trim()) return;
    const content = input.trim();
    setInput('');
    setError(null);

    // Optimistic: append the user turn locally + an empty assistant turn
    // we'll stream tokens into.
    const userTurnId = `local-${Date.now()}`;
    const assistantTurnId = `local-${Date.now() + 1}`;
    setMessages((prev) => [
      ...prev,
      { id: userTurnId, role: 'user', content },
      { id: assistantTurnId, role: 'assistant', content: '' },
    ]);
    setBusy(true);

    try {
      let acc = '';
      await streamChatTurn(
        { sessionId, content },
        {
          onSession: (id) => setSessionId(id),
          onToken: (text) => {
            acc += text;
            setMessages((prev) =>
              prev.map((m) => (m.id === assistantTurnId ? { ...m, content: acc } : m)),
            );
          },
          onDone: () => setBusy(false),
          onError: (msg) => {
            setError(msg);
            setBusy(false);
          },
        },
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reach the assistant.');
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open KEOBOOKS assistant"
        style={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          padding: '12px 18px',
          borderRadius: 999,
          background: 'var(--kb-brand-green, #3CA960)',
          color: '#fff',
          border: 'none',
          boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
          fontWeight: 600,
          fontSize: 14,
          cursor: 'pointer',
          zIndex: 1000,
        }}
      >
        Ask the assistant
      </button>
    );
  }

  return (
    <div
      role="dialog"
      aria-label="KEOBOOKS assistant"
      style={{
        position: 'fixed',
        right: 20,
        bottom: 20,
        width: 380,
        maxWidth: 'calc(100vw - 40px)',
        height: 520,
        maxHeight: 'calc(100vh - 80px)',
        display: 'flex',
        flexDirection: 'column',
        background: '#fff',
        border: '1px solid var(--kb-gray-200, #e5e7eb)',
        borderRadius: 12,
        boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
        zIndex: 1000,
      }}
    >
      <div
        style={{
          padding: '10px 14px',
          borderBottom: '1px solid var(--kb-gray-200, #e5e7eb)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <strong style={{ fontSize: 14 }}>Ask about your books</strong>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)} aria-label="Close">
          ×
        </Button>
      </div>

      <div
        ref={scrollerRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        {messages.length === 0 && (
          <div className="kb-muted" style={{ fontSize: 13, lineHeight: 1.5 }}>
            Try: <em>“What did I spend on rent last month?”</em> or{' '}
            <em>“Which transactions are pending my review?”</em>
          </div>
        )}
        {messages.map((m) => (
          <div
            key={m.id}
            style={{
              alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              padding: '8px 12px',
              borderRadius: 12,
              background:
                m.role === 'user'
                  ? 'var(--kb-brand-green, #3CA960)'
                  : 'var(--kb-gray-100, #f3f4f6)',
              color: m.role === 'user' ? '#fff' : 'var(--kb-gray-900, #111827)',
              fontSize: 14,
              lineHeight: 1.45,
              whiteSpace: 'pre-wrap',
            }}
          >
            {m.content || (m.role === 'assistant' && busy ? '…' : '')}
          </div>
        ))}
        {error && (
          <div role="alert" className="kb-form-error" style={{ fontSize: 13 }}>
            {error}
          </div>
        )}
      </div>

      <form
        onSubmit={send}
        style={{
          borderTop: '1px solid var(--kb-gray-200, #e5e7eb)',
          padding: 10,
          display: 'flex',
          gap: 8,
        }}
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.currentTarget.value)}
          placeholder="Ask anything…"
          disabled={busy}
          style={{
            flex: 1,
            padding: '8px 10px',
            borderRadius: 8,
            border: '1px solid var(--kb-gray-300, #d1d5db)',
            fontSize: 14,
          }}
        />
        <Button type="submit" size="sm" disabled={busy || !input.trim()}>
          {busy ? '…' : 'Send'}
        </Button>
      </form>
    </div>
  );
}
